import React, { useState } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, IconButton, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HousekeepingService } from '../api/services/housekeepingService';
import { useSelector } from 'react-redux';
import { toast } from '../utils/toast';

export default function ClaimItemModal({ open, onClose, onSuccess, item }) {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';

    const [formData, setFormData] = useState({
        claimedBy: '',
        claimedContact: '',
        idProofType: '',
        idProofNumber: '',
        notes: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset when opened
    React.useEffect(() => {
        if (open) {
            setFormData({
                claimedBy: '',
                claimedContact: '',
                idProofType: '',
                idProofNumber: '',
                notes: ''
            });
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.claimedBy || !formData.claimedContact || !formData.idProofType || !formData.idProofNumber) {
            toast.error("Please fill all required identification fields");
            return;
        }

        setIsSubmitting(true);
        try {
            // Strict Schema Clean-up Enforcement
            const payload = {
                claimedBy: formData.claimedBy,
                claimedContact: formData.claimedContact,
                idProofType: formData.idProofType,
                idProofNumber: formData.idProofNumber,
                notes: formData.notes
            };

            await HousekeepingService.claimLostFoundItem(item.id, payload, tenantId);
            toast.success("Item registry record marked as claimed successfully");
            
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "Failed to mark item as claimed");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!item) return null;

    return (
        <Modal open={open} onClose={isSubmitting ? undefined : onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 450 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={isSubmitting}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" mb={1}>
                    Claim Guest Item
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    Releasing: <b>{item.description}</b> (Found in Room {item.room?.roomNumber || item.roomId})
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Claimant Name *" 
                            name="claimedBy" 
                            value={formData.claimedBy} 
                            onChange={handleChange} 
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Contact Number *" 
                            name="claimedContact" 
                            value={formData.claimedContact} 
                            onChange={handleChange} 
                            size="small"
                            placeholder="+1 555-4321"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>ID Proof Type</InputLabel>
                            <Select 
                                name="idProofType" 
                                value={formData.idProofType} 
                                label="ID Proof Type" 
                                onChange={handleChange}
                            >
                                <MenuItem value="Passport">Passport</MenuItem>
                                <MenuItem value="Driver's License">Driver's License</MenuItem>
                                <MenuItem value="National ID">National ID</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField 
                            fullWidth 
                            label="ID Document Number *" 
                            name="idProofNumber" 
                            value={formData.idProofNumber} 
                            onChange={handleChange} 
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Release Notes" 
                            name="notes" 
                            multiline 
                            rows={3} 
                            value={formData.notes} 
                            onChange={handleChange} 
                            size="small"
                            placeholder="Returned to guest's brother upon verification"
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button fullWidth variant="outlined" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        color="primary"
                        onClick={handleSubmit} 
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
                    >
                        {isSubmitting ? 'Processing...' : 'Mark as Claimed'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
